/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import {
  Button,
  TextField,
  Checkbox2,
  FormCard,
  MessageTile,
} from "@suralink/samurai/ui";
import { Error } from "@suralink/samurai/icons";
import { SuraLogoWithText } from "@suralink/samurai/visuals";
import styles from "../../styles/CreateAccount.module.scss";
import * as actions from "../../redux/actions";
import CustomerResponsibilitiesModal from "./CustomerResponsibilitiesModal";

function FirmLogo({ logoUrl }: any) {
  return (
    <img
      src={logoUrl}
      alt="firm logo"
      style={{
        height: "100%",
        margin: "0 auto",
        maxHeight: "150px",
        maxWidth: "400px",
      }}
    />
  );
}

interface ComponentProps {
  throwUserErr: (state: boolean) => any;
  setUserErrMsg: (msg: string) => any;
  inviteId: string;
  dateFormat: any;
  timeFormat: any;
  timeZone: any;
}

interface StateProps {
  userErrState: boolean;
  createAccountInfo: any;
}

interface DispatchProps {
  updateUserInputAction: (field: string, value: string) => any;
  createAccountAction: (
    email: string,
    password: string,
    inviteId: string,
    firstName: string,
    lastName: string,
    dateFormat: any,
    timeZone: any,
    timeFormat: any,
    state: any
  ) => any;
}

type Props = StateProps & DispatchProps & ComponentProps;

const CreateAccountCard: React.FC<Props> = ({
  createAccountInfo,
  throwUserErr,
  setUserErrMsg,
  inviteId,
  dateFormat,
  timeFormat,
  timeZone,
  createAccountAction,
  updateUserInputAction,
  userErrState,
}) => {
  useEffect(() => {
    if (userErrState) {
      setPasswordError(false);
    }
  }, [userErrState]);

  const [userAgreementChecked, setUserAgreementChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [firstNameMissing, setFirstNameMissing] = useState(false);
  const [lastNameMissing, setLastNameMissing] = useState(false);
  const [passwordMissing, setPasswordMissing] = useState(false);
  const [confirmPasswordMissing, setConfirmPasswordMissing] = useState(false);
  const [emailMissing, setEmailMissing] = useState(false);
  const [customerResponsibilitiesOpen, setCustomerResponsibilitiesOpen] =
    useState(false);
  const urlState = createAccountInfo ? createAccountInfo.urlStateParam : null;

  const lengthCheck =
    createAccountInfo.password && createAccountInfo.password.length >= 8;
  const caseCheck =
    createAccountInfo.password &&
    !!createAccountInfo.password.match(/[A-Z]/) &&
    !!createAccountInfo.password.match(/[a-z]/);
  const alphaNumCheck =
    createAccountInfo.password &&
    !!createAccountInfo.password.match(/[0-9]/) &&
    !!createAccountInfo.password.match(/[a-z]/i);
  const specialCharCheck =
    createAccountInfo.password && !!createAccountInfo.password.match(/\W|_/);
  const matchCheck =
    createAccountInfo.password === createAccountInfo.passwordConfirmation &&
    createAccountInfo.password !== "" &&
    createAccountInfo.passwordConfirmation !== "";

  const checkAll =
    matchCheck && lengthCheck && caseCheck && alphaNumCheck && specialCharCheck;

  const checkForm = (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    confirmPassword: string
  ) => {
    if (
      !(firstName && firstName.trim().length) ||
      !(lastName && lastName.trim().length)
    ) {
      throwUserErr(true);
      setPasswordError(false);
      setUserErrMsg("All fields are required.");
      if (!firstName || !firstName.trim().length) {
        setFirstNameMissing(true);
      }
      if (!lastName || !lastName.trim().length) {
        setLastNameMissing(true);
      }
      if (!email || !email.trim().length) {
        setEmailMissing(true);
      }
      if (!password) {
        setPasswordMissing(true);
      }
      if (!createAccountInfo.passwordConfirmation) {
        setConfirmPasswordMissing(true);
      }
      return false;
    } else if (!password.length || !confirmPassword.length) {
      setPasswordError(true);
      throwUserErr(false);
      setErrorMessage("Password fields are required.");
      setPasswordMissing(true);
      setConfirmPasswordMissing(true);
      return false;
    } else if (password !== confirmPassword) {
      setPasswordError(true);
      throwUserErr(false);
      setErrorMessage("Passwords do not match.");
      return false;
    } else if (!checkAll) {
      setPasswordError(true);
      throwUserErr(false);
      setErrorMessage("Password does not match criteria.");
      return false;
    } else if (!userAgreementChecked) {
      throwUserErr(true);
      setPasswordError(false);
      setUserErrMsg(
        '"I have read and understand my responsibilities and the privacy policy."  -  is not optional.'
      );
      return false;
    }
    return true;
  };

  function submitForm(e: any) {
    e.preventDefault();
    throwUserErr(false);
    setPasswordError(false);

    const formValid: boolean = checkForm(
      createAccountInfo.firstName,
      createAccountInfo.lastName,
      createAccountInfo.email,
      createAccountInfo.password,
      createAccountInfo.passwordConfirmation
    );

    if (formValid) {
      createAccountAction(
        createAccountInfo.email.trim(),
        createAccountInfo.password,
        inviteId,
        createAccountInfo.firstName.trim(),
        createAccountInfo.lastName.trim(),
        dateFormat.value,
        timeZone.value,
        timeFormat.value,
        urlState
      );
    }
  }

  const formFilled =
    createAccountInfo.email &&
    createAccountInfo.password &&
    createAccountInfo.passwordConfirmation &&
    createAccountInfo.firstName &&
    createAccountInfo.lastName &&
    userAgreementChecked &&
    matchCheck;

  return (
    <FormCard
      style={{
        width: "475px",
        padding: "32px",
        margin: "0 8px",
      }}
    >
      {customerResponsibilitiesOpen && (
        <CustomerResponsibilitiesModal
          closeModal={() => setCustomerResponsibilitiesOpen(false)}
        />
      )}
      <div className={styles.header}>
        <div className={styles.logo}>
          {createAccountInfo.firmLogo ? (
            <FirmLogo logoUrl={createAccountInfo.firmLogo} />
          ) : (
            <SuraLogoWithText width={146} height={32} />
          )}
        </div>
        <hr />
        <div className={styles.title}>
          Welcome{" "}
          {createAccountInfo.initialFirstName &&
          createAccountInfo.initialLastName
            ? `${createAccountInfo.initialFirstName} ${createAccountInfo.initialLastName}`
            : null}
        </div>
        {createAccountInfo.firmName && (
          <div className={styles.subtitle}>
            You've been invited to join the {createAccountInfo.firmName} Team
          </div>
        )}
      </div>
      <div className={styles.form}>
        <form onSubmit={submitForm} autoComplete="off">
          <TextField
            value={createAccountInfo.firstName}
            disabled={createAccountInfo.id ? false : true}
            onChange={(e: any) =>
              updateUserInputAction("firstName", e.target.value)
            }
            onBlur={() => {
              if (createAccountInfo.firstName !== "") {
                setFirstNameMissing(false);
              }
            }}
            showRequiredState={firstNameMissing}
            requiredText=""
            placeholder="First Name"
            iconType="userIcon"
            label="First name"
            style={{
              margin: "32px auto",
            }}
          />
          <TextField
            value={createAccountInfo.lastName}
            disabled={createAccountInfo.id ? false : true}
            onChange={(e: any) =>
              updateUserInputAction("lastName", e.target.value)
            }
            onBlur={() => {
              if (createAccountInfo.lastName !== "") {
                setLastNameMissing(false);
              }
            }}
            showRequiredState={lastNameMissing}
            requiredText=""
            placeholder="Last Name"
            iconType="userIcon"
            label="Last name"
            style={{
              margin: "32px auto",
            }}
          />
          <TextField
            disabled={true}
            value={createAccountInfo.email}
            showRequiredState={emailMissing}
            type="email"
            iconType="mailIcon"
            label="Email"
            style={{
              margin: "32px auto",
            }}
          />
          {passwordError && (
            <MessageTile
              state="error"
              message={errorMessage}
              style={{}}
              icon={<Error />}
              dismissAlertCb={() => {
                setPasswordError(false);
              }}
            />
          )}
          <div className={styles.fieldWrapper}>
            <TextField
              value={createAccountInfo.password}
              disabled={createAccountInfo.id ? false : true}
              onChange={(e: any) =>
                updateUserInputAction("password", e.target.value)
              }
              onBlur={() => {
                if (createAccountInfo.password !== "") {
                  setPasswordMissing(false);
                }
              }}
              showRequiredState={passwordMissing || passwordError}
              requiredText=""
              type="password"
              placeholder="Password"
              iconType="lockIcon"
              label="Create password"
              style={{
                margin: "32px auto",
              }}
            />
          </div>
          <div className={styles.fieldWrapper}>
            <TextField
              value={createAccountInfo.passwordConfirmation}
              disabled={createAccountInfo.id ? false : true}
              onChange={(e: any) =>
                updateUserInputAction("passwordConfirmation", e.target.value)
              }
              onBlur={() => {
                if (createAccountInfo.passwordConfirmation !== "") {
                  setConfirmPasswordMissing(false);
                }
              }}
              showRequiredState={confirmPasswordMissing || passwordError}
              requiredText=""
              type="password"
              placeholder="Verify new password"
              iconType="lockIcon"
              label="Verify new password"
              style={{
                margin: "32px auto",
              }}
            />
          </div>
          <div className={classNames(styles.row, styles.privacyPolicy)}>
            <Checkbox2
              checked={userAgreementChecked}
              onClick={() => setUserAgreementChecked(!userAgreementChecked)}
            />
            <p className={styles.privacyPolicyText}>
              I have read and understand my{" "}
              <span
                className={styles.link}
                onClick={() => setCustomerResponsibilitiesOpen(true)}
              >
                responsibilities
              </span>{" "}
              and the{" "}
              <a
                className={styles.link}
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.suralink.com/privacy-policy"
              >
                privacy policy
              </a>
              .
            </p>
          </div>
          <Button
            label="Create account"
            type="submit"
            disabled={!formFilled}
            color="blue"
            className={styles.submitButton}
            onClick={() => {}}
          />
        </form>
      </div>
    </FormCard>
  );
};

const mstp = (state: InitialStateModel): StateProps => {
  const { userErrState, createAccountInfo } = state;
  return {
    userErrState,
    createAccountInfo,
  };
};

const matp = ((): DispatchProps => {
  const { createAccountAction, updateUserInputAction } = actions;
  return {
    createAccountAction,
    updateUserInputAction,
  };
})();

export default connect(mstp, matp)(CreateAccountCard);
